import React, { useState } from 'react';
import { Fix } from '../types/validation';
import './FixPreview.css';

interface FixPreviewProps {
  fixes: Fix[];
  currentCode: string;
  onApply: (selectedFixes: Fix[]) => void;
  onCancel: () => void;
  isApplying?: boolean;
}

const FixPreview: React.FC<FixPreviewProps> = ({
  fixes,
  currentCode,
  onApply,
  onCancel,
  isApplying = false
}) => {
  const [selectedFixes, setSelectedFixes] = useState<Set<number>>(
    new Set(fixes.map((_, index) => index))
  );

  const toggleFix = (index: number) => {
    const newSelected = new Set(selectedFixes);
    if (newSelected.has(index)) {
      newSelected.delete(index);
    } else {
      newSelected.add(index);
    }
    setSelectedFixes(newSelected);
  };

  const handleApply = () => {
    const fixesToApply = fixes.filter((_, index) => selectedFixes.has(index));
    onApply(fixesToApply);
  };

  // Generate preview of code changes
  const generatePreview = (fix: Fix): { before: string[], after: string[] } => {
    const lines = currentCode.split('\n');
    const before: string[] = [];
    const after: string[] = [];
    
    // Find occurrences
    lines.forEach((line, index) => {
      if (line.includes(fix.find)) {
        before.push(`${index + 1}: ${line}`);
        
        // Replace ALL occurrences in the line, not just the first
        let modifiedLine = line;
        let startIndex = 0;
        
        // Keep replacing until no more occurrences found
        while (modifiedLine.indexOf(fix.find, startIndex) !== -1) {
          const foundIndex = modifiedLine.indexOf(fix.find, startIndex);
          modifiedLine = modifiedLine.substring(0, foundIndex) + 
                        fix.replace + 
                        modifiedLine.substring(foundIndex + fix.find.length);
          startIndex = foundIndex + fix.replace.length;
        }
        
        after.push(`${index + 1}: ${modifiedLine}`);
      }
    });

    return { before, after };
  };

  return (
    <div className="fix-preview">
      <div className="fix-preview-header">
        <h3>Review Proposed Fixes</h3>
        <p className="fix-preview-summary">
          {selectedFixes.size} of {fixes.length} fixes selected
        </p>
      </div>

      <div className="fix-list">
        {fixes.map((fix, index) => {
          const { before, after } = generatePreview(fix);
          const isSelected = selectedFixes.has(index);

          return (
            <div key={index} className={`fix-item ${isSelected ? 'selected' : ''}`}>
              <div className="fix-item-header">
                <label className="fix-checkbox">
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleFix(index)}
                    disabled={isApplying}
                  />
                  <span className="fix-reason">{fix.reason}</span>
                </label>
              </div>

              {isSelected && (
                <div className="fix-diff">
                  <div className="diff-section">
                    <div className="diff-header before">Before</div>
                    <code className="diff-code before">
                      {before.map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </code>
                  </div>
                  
                  <div className="diff-section">
                    <div className="diff-header after">After</div>
                    <code className="diff-code after">
                      {after.map((line, i) => (
                        <div key={i}>{line}</div>
                      ))}
                    </code>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="fix-preview-actions">
        <button
          className="action-button apply-button"
          onClick={handleApply}
          disabled={isApplying || selectedFixes.size === 0}
        >
          {isApplying ? (
            <>
              <span className="spinner">⚙️</span> Applying...
            </>
          ) : (
            <>✓ Apply Selected Fixes</>
          )}
        </button>
        
        <button
          className="action-button cancel-button"
          onClick={onCancel}
          disabled={isApplying}
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default FixPreview;